import { css } from 'linaria';
import { styled } from 'linaria/react';
import React, { useContext } from 'react';

import t from '@jetshop/intl';
import ChannelContext from '@jetshop/core/components/ChannelContext/ChannelContext';
import { ReactComponent as Check } from '../../svg/Check.svg';
import { ReactComponent as RedCross } from '../../svg/Cross.svg';
import { useFavoriteStore } from '../../contexts/FavoriteStoreContext.js';
import { theme } from '../Theme';
import { useElevate } from './ElevateContext.jsx';

const AvailabilityWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  gap: 4px;

  ${theme.below.md} {
    flex-direction: row !important;
    align-items: flex-start;
    justify-content: flex-start;
    margin-bottom: 8px;
  }

  .location {
    position: relative;
    display: flex;
    align-items: center;
    p {
      text-transform: uppercase;
      margin-right: 3px;
      font-size: 10px;
      color: black;
      line-height: 1;
      font-weight: 700;
      letter-spacing: 0.08em;
      font-weight: 600;
    }

    &.webshop {
      p {
        white-space: nowrap;
      }
    }
  }

  .store-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  @media screen and (max-width: 430px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const checkStyle = css`
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  color: #4cd038;
  use {
    fill: #4cd038;
  }
  margin-top: -2px;
`;

const redCrossStyle = css`
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  color: red;
  use {
    fill: red;
  }
`;

const CHANNEL_ONLINE = 'ONLINE';
const CHANNEL_STORE = 'STORE';

// Elevate availability.key is a string; Jetshop store.id is an int.
const sameStoreId = (a, b) => a != null && b != null && String(a) === String(b);

const webAndStoreStockStatus = (product, favoriteStore, listFacetStore) => {
  let webStock = false;
  let storeStock = false;
  let favoriteStoreStock = false;
  let listFacetStoreStock = false;

  product?.variants?.forEach(variant => {
    variant?.availability?.forEach(availability => {
      if (availability.channel === CHANNEL_ONLINE) {
        if (availability?.stockNumber > 0) {
          webStock = true;
        }
      }
      if (availability.channel === CHANNEL_STORE) {
        if (availability?.stockNumber > 0) {
          storeStock = true;
        }
      }
      if (
        favoriteStore &&
        sameStoreId(availability.key, favoriteStore.id)
      ) {
        if (availability?.stockNumber > 0) {
          favoriteStoreStock = true;
        }
      }
      if (
        listFacetStore &&
        (sameStoreId(availability.key, listFacetStore.id) ||
          sameStoreId(availability.key, listFacetStore.externalId))
      ) {
        if (availability?.stockNumber > 0) {
          listFacetStoreStock = true;
        }
      }
    });
  });

  return {
    webStock,
    storeStock,
    favoriteStoreStock,
    listFacetStoreStock
  };
};

export const ElevateAvailability = ({ product }) => {
  const { selectedChannel } = useContext(ChannelContext);
  const { favoriteStore, getAllStores } = useFavoriteStore();
  const { listFacetStoreId } = useElevate();
  const stores = getAllStores();

  const listFacetStore = stores?.find(
    store => String(store?.id) === String(listFacetStoreId)
  );

  const hasNoStores = selectedChannel?.id === 2 || selectedChannel?.id === 6;
  const isGiftCard = product?.isGiftCard;

  const { webStock, storeStock, favoriteStoreStock, listFacetStoreStock } =
    webAndStoreStockStatus(product, favoriteStore, listFacetStore);

  return (
    <AvailabilityWrapper className="availability-wrapper">
      <div className="location webshop">
        <p>{t('Webshop')}</p>
        {webStock ? (
          <Check className={checkStyle} />
        ) : (
          <RedCross className={redCrossStyle} />
        )}
      </div>

      {listFacetStoreId ? (
        <div className="location store">
          <p>{listFacetStore?.name}</p>
          {listFacetStoreStock ? (
            <Check className={checkStyle} />
          ) : (
            <RedCross className={redCrossStyle} />
          )}
        </div>
      ) : (
        <>
          {!hasNoStores && favoriteStore ? (
            <div className="location store">
              <p>{favoriteStore?.name}</p>
              {favoriteStoreStock || isGiftCard ? (
                <Check className={checkStyle} />
              ) : (
                <RedCross className={redCrossStyle} />
              )}
            </div>
          ) : (
            <div className="location store">
              <p>{t('Store')}</p>
              {storeStock || isGiftCard ? (
                <Check className={checkStyle} />
              ) : (
                <RedCross className={redCrossStyle} />
              )}
            </div>
          )}
        </>
      )}
    </AvailabilityWrapper>
  );
};
